"use client";

import { useState } from "react";
import { Box, Button, Collapse, Link, Typography } from "@mui/material";
import SouthIcon from "@mui/icons-material/South";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { SponsorCampaign } from "../campaignTypes";
import { PORTAL_MAX_WIDTH, portalFont, portalShellSx } from "../partnerTheme";
import AirbnbBrandOverview from "./AirbnbBrandOverview";

const ACCENT = "#FF5A5F";
const LINE = "rgba(255,255,255,0.12)";
const MUTED = "rgba(244,245,247,0.62)";
const CARD_BG = "rgba(255,255,255,0.04)";
const RADIUS = "16px";

const IMG = {
  tickets: "https://storage.googleapis.com/cuo-website/ESB%20Tickets.jpg",
  food: "https://storage.googleapis.com/cuo-website/Food%20at%20ESB.jpg",
  socializing: "https://storage.googleapis.com/cuo-website/Socializing%20at%20ESB.jpg",
  crowd: "https://storage.googleapis.com/cuo-website/Crowd%20shot%20esb.jpg",
  tourGuide: "https://storage.googleapis.com/cuo-website/Tour%20Guide%20ESB.jpg",
  esbTopDeck: "https://storage.googleapis.com/cuo-website/ESB%20Top%20Deck.jpg",
  skyline: "https://storage.googleapis.com/cuo-website/People%20looking%20out%20at%20Skyline.jpg",
  observatoryCouple:
    "https://storage.googleapis.com/cuo-website/Observatory%20deck%20social.jpg",
  obsTour3: "https://storage.googleapis.com/cuo-website/ESB%20Observatory%20Tour%203.jpg",
  sam: "https://storage.googleapis.com/cuo-website/Sam%20Morril%20ESB.jpg",
  chris: "https://storage.googleapis.com/cuo-website/Chris%20ESB.jpg",
  zarna: "https://storage.googleapis.com/cuo-website/Zarna%20at%20ESB%20.jpg",
  emma: "https://storage.googleapis.com/cuo-website/Emma%20Willmann%20at%20ESB.jpg",
  matt: "https://storage.googleapis.com/cuo-website/Matt%20Richards%20at%20ESB.jpg",
  lenny: "https://storage.googleapis.com/cuo-website/Lenny%20Marcus.jpg",
  katz: "https://storage.googleapis.com/cuo-website/Katz%27s+Deli.jpg",
  katzEsb: "https://storage.googleapis.com/cuo-website/Katz's%20ESB.jpg",
  distefanoEsb: "https://storage.googleapis.com/cuo-website/Distefano%20ESB.jpg",
  tourEsb: "https://storage.googleapis.com/cuo-website/UO%20at%20ESB%2091%20May%203%202026.jpeg",
  obsDeck: "https://storage.googleapis.com/cuo-website/ESB%20Obs%20Deck.jpg",
  stTheos: "https://storage.googleapis.com/cuo-website/St.%20Theos.jpeg",
  capitalOneLogo: "/images/partners/airbnb/logo.png",
  comedyuoLogo: "https://storage.googleapis.com/cuo-website/comedyuo-logo-white.png",
} as const;

const NAV = [
  { id: "residency", label: "The residency" },
  { id: "evening", label: "The evening" },
  { id: "first-show", label: "The first show" },
  { id: "airbnb-difference", label: "The Airbnb difference" },
  { id: "partner-model", label: "Partner model" },
  { id: "other-ways", label: "Other ways" },
] as const;

const FACTS = [
  { label: "Series", value: "4 February shows · 6:45–10 PM" },
  { label: "Planned audience", value: "1,100 guests across the series" },
  { label: "Programming", value: "20 comics across four nights" },
  { label: "Show dates", value: "February 4, 11, 18 & 25 · 2027" },
] as const;

const DATES = [
  { day: "04", show: "Show 1" },
  { day: "11", show: "Show 2" },
  { day: "18", show: "Show 3" },
  { day: "25", show: "Show 4" },
] as const;

const EVENING = [
  {
    time: "6:45 PM",
    title: "Arrival, food, and cocktails",
    body: "Light bites and drinks as guests mingle and find their seats.",
    images: [
      {
        src: IMG.katzEsb,
        alt: "Katz's hospitality at the Empire State Building show",
      },
      {
        src: IMG.food,
        alt: "Food and drinks during arrival at the Empire State Building",
      },
    ],
  },
  {
    time: "7:30 PM",
    title: "The show",
    body: "Four comics and one emcee perform; the bar remains open.",
    images: [
      {
        src: IMG.distefanoEsb,
        alt: "Chris Distefano performing at the Empire State Building",
      },
      {
        src: IMG.sam,
        alt: "Sam Morril performing at the Empire State Building",
      },
    ],
  },
  {
    time: "8:45 PM",
    title: "After the laughs",
    body: "Cocktails and conversation resume; the observatory tour begins.",
    images: [
      {
        src: IMG.tourEsb,
        alt: "ComedyUO tour group at the Empire State Building",
      },
      {
        src: IMG.tourGuide,
        alt: "Tour guide on the Empire State Building observatory",
      },
    ],
  },
  {
    time: "10:00 PM",
    title: "The night concludes",
    body: "The observatory tour closes and guests depart.",
    images: [
      {
        src: IMG.obsDeck,
        alt: "Empire State Building observatory deck",
      },
      {
        src: IMG.skyline,
        alt: "Guests looking out at the New York skyline",
      },
    ],
  },
] as const;

const DIFFERENCE_ROWS = [
  {
    moment: "The anticipation",
    residency: "Gets a confirmation email that they made the list.",
    capitalOne:
      "Receives a customized ticket sent before their show, tailored to them, and also receives the complimentary tickets given out at the show.",
  },
  {
    moment: "Priority access",
    residency:
      "The avg. member of our waitlist competes with 39,000 other members to get a ticket.",
    capitalOne: "Protected ticket allocation for Airbnb guests, ~20 tickets per show.",
  },
  {
    moment: "VIP experience",
    residency: "Gets to sit within 5 rows of some of comedy's biggest names.",
    capitalOne:
      "Select Airbnb guests get to meet the talent backstage and in the green room before they perform.",
  },
  {
    moment: "The cherry on top",
    residency: "Gets a behind-the-scenes tour up to the observatory deck.",
    capitalOne:
      "Gets a more exclusive, all-access tour of the building beyond just the observatory deck.",
  },
  {
    moment: "Walking out",
    residency: "Leaves with a full belly of Katz's.",
    capitalOne: "Gets a complimentary ticket to another ComedyUO show of their choice.",
  },
] as const;

const CASE_CHAPTERS = [
  {
    title: "The lineup",
    figure: "5",
    figureLabel: "comedians",
    copy: "Sam Morril, Chris Distefano, Zarna Garg, Emma Willmann, Matt Richards, and Lenny Marcus opened a room that had never hosted comedy before. The night landed hard enough that we are coming back for a residency.",
    imageSrc: IMG.sam,
    imageAlt: "Sam Morril on stage at the Empire State Building",
  },
  {
    title: "The room & attendance",
    figure: "225",
    figureLabel: "guests",
    copy: "An intimate comedy room inside the landmark, packed end to end. Tickets, seats, and a crowd that felt like a private show in a public icon.",
    imageSrc: IMG.crowd,
    imageAlt: "Crowd at the Empire State Building comedy show",
  },
  {
    title: "Food & hospitality",
    figure: "Katz's Deli",
    figureLabel: "on the menu",
    copy: "World-famous food for a world-famous landmark. We called up Katz's to cater the night and bring two New York icons together.",
    imageSrc: "https://storage.googleapis.com/cuo-website/Katz's%20Bite.jpg",
    imageAlt: "Katz's food at the Empire State Building comedy show",
  },
] as const;

const MARCH_LINEUP = [
  {
    name: "Sam Morril",
    note: "March ESB",
    src: IMG.sam,
    instagram: "https://www.instagram.com/sammorril/",
    objectPosition: "center 20%",
  },
  {
    name: "Chris Distefano",
    note: "March ESB",
    src: IMG.chris,
    instagram: "https://www.instagram.com/chrisdcomedy/?hl=en",
    objectPosition: "center 18%",
  },
  {
    name: "Zarna Garg",
    note: "March ESB",
    src: IMG.zarna,
    instagram: "https://www.instagram.com/zarnagarg/",
    objectPosition: "center 22%",
  },
  {
    name: "Emma Willmann",
    note: "March ESB",
    src: IMG.emma,
    instagram: "https://www.instagram.com/emmawillmann/",
    objectPosition: "center 18%",
  },
  {
    name: "Matt Richards",
    note: "March ESB",
    src: IMG.matt,
    instagram: "https://www.instagram.com/mattwasfunny/",
    objectPosition: "center 20%",
  },
  {
    name: "Lenny Marcus",
    note: "March ESB",
    src: IMG.lenny,
    instagram: "https://www.instagram.com/lennymarcusnyc/",
    objectPosition: "center 15%",
  },
] as const;



const CARRIER_TICKETS = [
  "Sizable reserved ticket allocation for customers",
  "Dedicated seating signage at the event",
  "Cut the line access",
  "Option to distribute tickets and market the night through their app or interface",
  "Right to promote a headliner and use comedian name, image, and likeness in their app",
] as const;

const UNITED_RESERVED = [
  "Reserved tickets for Airbnb guests across the series",
  "Custom tickets and pre-show packages shaped for Airbnb guests",
  "Reserved Airbnb guest seating block with member-only markers",
  "Option to distribute tickets and market the residency through their app or interface",
  "Potential to promote a headliner and use comedian name, image, and likeness",
] as const;

const UNITED_VIP = [
  "Green-room access for VIP guests",
  "Talent memorabilia and Airbnb-branded photo moments",
] as const;

/**
 * Custom Airbnb Empire State Residency partnership concept page. Separate from the other partner portals.
 */
export default function AirbnbPortalPage({ campaign }: { campaign: SponsorCampaign }) {
  const [tab, setTab] = useState<"overview" | "residency">("overview");

  function openTab(next: "overview" | "residency") {
    setTab(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <Box sx={{ ...portalShellSx(campaign), bgcolor: "#000" }}>
      <Header
        tab={tab}
        onTabChange={openTab}
        residencyCtaHref={tab === "residency" ? "#partner-model" : undefined}
      />
      {tab === "overview" ? (
        <AirbnbBrandOverview onContinueToResidency={() => openTab("residency")} />
      ) : (
        <>
          <Hero />
          <ResidencySection />
          <EveningSection />
          <FirstShowSection />
          <CapitalOneDifferenceSection />
          <PartnerModelSection />
          <AirbnbExtraSection />
        </>
      )}
      <Footer contactEmail={campaign.closing.contact.email} />
    </Box>
  );
}

function Header({
  tab,
  onTabChange,
  residencyCtaHref,
}: {
  tab: "overview" | "residency";
  onTabChange: (tab: "overview" | "residency") => void;
  residencyCtaHref?: string;
}) {
  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        bgcolor: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(14px)",
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          py: { xs: 1.25, md: 1.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: { xs: 1.25, md: 1.75 } }}>
          <CapitalOneLogo height={{ xs: 32, md: 42 }} />
          <Box
            aria-hidden
            sx={{
              width: "1px",
              height: { xs: 28, md: 36 },
              bgcolor: "rgba(255,255,255,0.35)",
              flexShrink: 0,
            }}
          />
          <Box
            component="img"
            src={IMG.comedyuoLogo}
            alt="ComedyUO"
            sx={{
              height: { xs: 40, md: 56 },
              width: "auto",
              display: "block",
              flexShrink: 0,
              objectFit: "contain",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 0.4,
            borderRadius: "999px",
            border: `1px solid ${LINE}`,
            bgcolor: "rgba(255,255,255,0.04)",
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          {(
            [
              { id: "overview", label: "ComedyUO" },
              { id: "residency", label: "The residency" },
            ] as const
          ).map((item) => {
            const active = tab === item.id;
            return (
              <Button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.75rem", md: "0.8125rem" },
                  color: active ? "#0a0a0a" : MUTED,
                  bgcolor: active ? "#fff" : "transparent",
                  borderRadius: "999px",
                  px: { xs: 1.25, md: 2 },
                  py: 0.7,
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    bgcolor: active ? "#fff" : "rgba(255,255,255,0.06)",
                    color: active ? "#0a0a0a" : "#fff",
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        {residencyCtaHref ? (
          <Button
            href={residencyCtaHref}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              fontFamily: portalFont,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontSize: "0.6875rem",
              color: "#fff",
              border: `1px solid rgba(255,255,255,0.45)`,
              borderRadius: "999px",
              px: 2,
              py: 0.85,
              flexShrink: 0,
              "&:hover": {
                borderColor: "#fff",
                bgcolor: "rgba(255,255,255,0.06)",
              },
            }}
          >
            Partner model
          </Button>
        ) : (
          <Box sx={{ display: { xs: "none", sm: "block" }, width: 88, flexShrink: 0 }} aria-hidden />
        )}
      </Box>

      {tab === "residency" && (
        <Box
          sx={{
            display: { xs: "none", xl: "block" },
            borderTop: `1px solid ${LINE}`,
          }}
        >
          <Box
            component="nav"
            sx={{
              maxWidth: PORTAL_MAX_WIDTH,
              mx: "auto",
              px: { xs: 2.5, md: 4 },
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 2,
              overflowX: "auto",
            }}
          >
            {NAV.map((item) => (
              <Link
                key={item.id}
                href={`#${item.id}`}
                underline="none"
                sx={{
                  fontFamily: portalFont,
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: MUTED,
                  whiteSpace: "nowrap",
                  "&:hover": { color: "#fff" },
                }}
              >
                {item.label}
              </Link>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Hero() {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        minHeight: { xs: "88vh", md: "92vh" },
        display: "flex",
        alignItems: "stretch",
        isolation: "isolate",
      }}
    >
      <Box
        component="img"
        src={IMG.tickets}
        alt="Empire State Building comedy show tickets held in the air"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: { xs: "center center", md: "center 40%" },
          zIndex: 0,
          filter: "brightness(0.62) contrast(1.05)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background: {
            xs: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.72) 45%, rgba(0,0,0,0.94) 100%)",
            md: "linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.72) 42%, rgba(0,0,0,0.28) 72%, rgba(0,0,0,0.55) 100%), linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.85) 100%)",
          },
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: PORTAL_MAX_WIDTH,
          width: "100%",
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          pt: { xs: 8, md: 14 },
          pb: { xs: 5, md: 6 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: { xs: "88vh", md: "92vh" },
        }}
      >
        <Box sx={{ maxWidth: 640 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: "clamp(2.35rem, 6.5vw + 0.4rem, 4.75rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.035em",
              mb: { xs: 2, md: 2.5 },
            }}
          >
            The Empire State Residency.
          </Typography>

          <Typography
            sx={{
              fontFamily: portalFont,
              color: "rgba(244,245,247,0.82)",
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.65,
              maxWidth: 520,
              mb: { xs: 3, md: 3.5 },
            }}
          >
            The first-ever comedy residency at the Empire State Building. Four February shows,
            twenty comics, and a way for Airbnb guests to get into the room.
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
            <PillButton href="#residency" solid>
              See the story
            </PillButton>
            <PillButton href="#airbnb-difference">The Airbnb difference</PillButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 2,
            mt: 6,
          }}
        >
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.625rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)",
              maxWidth: 360,
            }}
          >
            Photographs from the first Empire State Building comedy show · March
          </Typography>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              color: "rgba(255,255,255,0.55)",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.625rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Scroll
            </Typography>
            <Box sx={{ width: "1px", height: 48, bgcolor: "rgba(255,255,255,0.45)" }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ResidencySection() {
  return (
    <Section id="residency">
      <Eyebrow accent>The residency</Eyebrow>
      <Title>Four nights. One unforgettable address.</Title>
      <Body sx={{ maxWidth: 720, mb: { xs: 4, md: 5 } }}>
        We transform iconic spaces into unique, after-hours comedy venues. Now we are
        heading to the most iconic building in the world for a winter residency: stand-up, open-bar
        hospitality, and an observatory tour, four nights in February 2027.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
          borderTop: `1px solid ${LINE}`,
          borderBottom: `1px solid ${LINE}`,
          mb: { xs: 7, md: 10 },
        }}
      >
        {FACTS.map((fact, i) => (
          <Box
            key={fact.label}
            sx={{
              px: { xs: 0, md: 3 },
              py: { xs: 2.5, md: 3.5 },
              borderRight: {
                xs: "none",
                sm: i % 2 === 0 ? `1px solid ${LINE}` : "none",
                md: i < FACTS.length - 1 ? `1px solid ${LINE}` : "none",
              },
              borderBottom: {
                xs: i < FACTS.length - 1 ? `1px solid ${LINE}` : "none",
                sm: i < 2 ? `1px solid ${LINE}` : "none",
                md: "none",
              },
              pl: { md: i === 0 ? 0 : 3 },
              pr: { md: i === FACTS.length - 1 ? 0 : 3 },
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: MUTED,
                mb: 1.25,
              }}
            >
              {fact.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 600,
                color: "#fff",
                fontSize: { xs: "1rem", md: "1.0625rem" },
                lineHeight: 1.4,
              }}
            >
              {fact.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Eyebrow accent>Four dates · February 2027</Eyebrow>
      <Title sx={{ mb: { xs: 3, md: 4 } }}>One residency, four nights.</Title>
      <Box sx={{ borderTop: `1px solid ${LINE}` }}>
        {DATES.map((d) => (
          <Box
            key={d.day}
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 2,
              py: { xs: 2.25, md: 2.75 },
              borderBottom: `1px solid ${LINE}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                }}
              >
                {d.day}
              </Typography>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  color: MUTED,
                  fontSize: { xs: "1rem", md: "1.125rem" },
                }}
              >
                February
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: MUTED,
              }}
            >
              {d.show}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function EveningSection() {
  return (
    <Section id="evening" sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 5, md: 6 } }}>
      <Eyebrow accent>The evening · 6:45–10 PM</Eyebrow>
      <Title>How each residency night runs.</Title>
      <Body sx={{ maxWidth: 680, mb: { xs: 3, md: 4 } }}>
        Same arc every night. Arrival, food, and cocktails, the show, post-show mingling, then up to
        the observatory.
      </Body>

      <Box
        sx={{
          position: "relative",
          maxWidth: 1100,
          py: { xs: 0.5, md: 1 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: { xs: 11, md: 15 },
            top: 12,
            bottom: 12,
            width: 0,
            borderLeft: "2px dotted rgba(255,255,255,0.28)",
            zIndex: 0,
          }}
        />

        <Box sx={{ display: "grid", gap: { xs: 4, md: 5.5 }, position: "relative", zIndex: 1 }}>
          {EVENING.map((step) => (
            <TrailStopCard key={step.time} step={step} />
          ))}
        </Box>
      </Box>
    </Section>
  );
}

function TrailStopCard({
  step,
}: {
  step: (typeof EVENING)[number];
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "24px 1fr", md: "32px 1fr" },
        columnGap: { xs: 1.5, md: 2.5 },
        alignItems: "start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pt: { xs: 0.35, md: 0.55 },
          position: "relative",
          zIndex: 2,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: { xs: 14, md: 16 },
            height: { xs: 14, md: 16 },
            borderRadius: "50%",
            bgcolor: "#111111",
            border: "3px solid #000",
            boxShadow: "0 0 0 3px rgba(0,111,207,0.45)",
            flexShrink: 0,
          }}
        />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: { xs: 0.75, md: 1.25 },
            mb: { xs: 1.25, md: 1.5 },
          }}
        >
          <Typography
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: ACCENT,
              fontSize: { xs: "1.35rem", md: "1.6rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1,
            }}
          >
            {step.time}
          </Typography>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: { xs: "1.05rem", md: "1.25rem" },
              lineHeight: 1.15,
            }}
          >
            {step.title}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 1, md: 1.25 },
            mb: { xs: 1.25, md: 1.5 },
          }}
        >
          {step.images.map((image) => (
            <Box
              key={image.src}
              sx={{
                borderRadius: { xs: "14px", md: "18px" },
                overflow: "hidden",
                border: `1px solid ${LINE}`,
                bgcolor: "#121212",
                aspectRatio: { xs: "16 / 10", sm: "4 / 3" },
                minHeight: { xs: 200, sm: 240, md: 300 },
              }}
            >
              <Box
                component="img"
                src={image.src}
                alt={image.alt}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            fontFamily: portalFont,
            color: MUTED,
            fontSize: { xs: "0.9375rem", md: "1rem" },
            lineHeight: 1.5,
            maxWidth: 720,
          }}
        >
          {step.body}
        </Typography>
      </Box>
    </Box>
  );
}

function CapitalOneDifferenceSection() {
  return (
    <Section id="airbnb-difference" sx={{ pt: { xs: 4, md: 6 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <CapitalOneLogo height={{ xs: 28, md: 34 }} />
        <Eyebrow accent sx={{ mb: 0 }}>The Airbnb opportunity</Eyebrow>
      </Box>
      <Title>The city’s newest residency. The Airbnb inside track.</Title>
      <Body sx={{ maxWidth: 760, mb: { xs: 4, md: 5 } }}>
        The residency already has a compelling arc. An Airbnb partnership could add a distinct,
        recognizable Airbnb guest layer at each moment. This is how the experience can differ from a
        typical waitlist guest.
      </Body>

      <Box sx={{ borderTop: `1px solid ${LINE}`, display: { xs: "none", md: "block" } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "0.7fr 1.15fr 1.35fr",
            gap: 3,
            py: 2,
            borderBottom: `1px solid ${LINE}`,
          }}
        >
          {["Moment", "The avg. attendee", "Airbnb guests · Proposed"].map((h, i) => (
            <Typography
              key={h}
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: i === 2 ? ACCENT : MUTED,
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>
        {DIFFERENCE_ROWS.map((row) => (
          <Box
            key={row.moment}
            sx={{
              display: "grid",
              gridTemplateColumns: "0.7fr 1.15fr 1.35fr",
              gap: 3,
              py: 3,
              borderBottom: `1px solid ${LINE}`,
              alignItems: "start",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: "1.125rem",
              }}
            >
              {row.moment}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.55 }}>
              {row.residency}
            </Typography>
            <Box sx={{ borderLeft: `2px solid ${ACCENT}`, pl: 2.5 }}>
              <Typography sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.55 }}>
                {row.capitalOne}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: { xs: "grid", md: "none" }, gap: 2.5 }}>
        {DIFFERENCE_ROWS.map((row) => (
          <Box key={row.moment} sx={{ borderTop: `1px solid ${LINE}`, pt: 2.5 }}>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: "1.125rem",
                mb: 1.5,
              }}
            >
              {row.moment}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: MUTED,
                mb: 0.75,
              }}
            >
              The avg. attendee
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, mb: 1.75, lineHeight: 1.55 }}>
              {row.residency}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: ACCENT,
                mb: 0.75,
              }}
            >
              Airbnb guests · Proposed
            </Typography>
            <Box sx={{ borderLeft: `2px solid ${ACCENT}`, pl: 2 }}>
              <Typography sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.55 }}>
                {row.capitalOne}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function FirstShowSection() {
  return (
    <Section id="first-show">
      <Eyebrow accent>Case study · March</Eyebrow>
      <Title>The first Empire State Building show</Title>
      <Body sx={{ maxWidth: 760, mb: { xs: 4, md: 5 } }}>
        In March we threw the first comedy show the Empire State Building has ever seen, and it was
        incredible. Here is how the night went: the lineup, the room, the hospitality, and the
        observatory finish. Press from Time Out NY and CBS NYC followed.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: { xs: 2, md: 2.5 },
          mb: { xs: 5, md: 7 },
        }}
      >
        {CASE_CHAPTERS.map((card) => (
          <CaseFigureCard key={card.title} card={card} />
        ))}
      </Box>

      <Panel>
        <Eyebrow accent>After the set</Eyebrow>
        <Typography
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: { xs: "1.35rem", md: "1.65rem" },
            letterSpacing: "-0.02em",
            mb: 1.25,
          }}
        >
          Observatory tour
        </Typography>
        <Body sx={{ mb: 2.5, maxWidth: 720 }}>
          Guests moved from the room up to the deck: the Empire State Building arch, skyline
          views, and a night that ended above the city.
        </Body>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "1.15fr 1fr 1fr" },
            gap: { xs: 1.5, md: 1.75 },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: `1px solid ${LINE}`,
              bgcolor: "#121212",
              aspectRatio: "1 / 1",
              gridColumn: { xs: "1 / -1", md: "auto" },
            }}
          >
            <Box
              component="img"
              src={IMG.observatoryCouple}
              alt="Guests posing under the Empire State Building observatory arch"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 40%",
                display: "block",
              }}
            />
          </Box>
          <MiniPhoto src={IMG.esbTopDeck} alt="Empire State Building observatory top deck" />
          <MiniPhoto
            src={IMG.obsTour3}
            alt="Guests on the Empire State Building observatory tour"
          />
        </Box>
      </Panel>

      <Box sx={{ mt: { xs: 6, md: 8 } }}>
        <Eyebrow accent>March lineup</Eyebrow>
        <Title sx={{ mb: 1.5 }}>The comics who opened the building.</Title>
        <Body sx={{ maxWidth: 680, mb: { xs: 3, md: 4 } }}>
          Comics from the first ESB show. Tap a card for Instagram.
        </Body>
        <MarchLineupGrid />
      </Box>
    </Section>
  );
}

function PartnerModelSection() {
  return (
    <Section id="partner-model">
      <Eyebrow accent>How we work with partners</Eyebrow>
      <Title>An Airbnb guest layer, proven at Katz&apos;s with a wireless carrier partner.</Title>
      <Body sx={{ maxWidth: 760, mb: { xs: 4, md: 5 } }}>
        Here is how partner access works today. At Katz&apos;s, our wireless carrier
        partner gets a sizable ticket allocation for customers. For the residency, Airbnb
        can go further: reserved Airbnb guest tickets, custom packages, and a VIP layer with
        green-room access and talent memorabilia.
      </Body>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 2, md: 2.5 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Panel>
          <Box
            component="img"
            src={IMG.katz}
            alt="Katz's Delicatessen, ComedyUO venue"
            sx={{
              width: "100%",
              height: { xs: 180, md: 220 },
              objectFit: "cover",
              borderRadius: "10px",
              mb: 2.25,
              display: "block",
            }}
          />
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: ACCENT,
              mb: 1,
            }}
          >
            Example · Katz&apos;s × Wireless Carrier Partner · One evening
          </Typography>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: { xs: "1.25rem", md: "1.5rem" },
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            Sizable ticket allocation for customers
          </Typography>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: MUTED,
              mb: 1.25,
            }}
          >
            Reserved tickets · Sizable allocation
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
            {CARRIER_TICKETS.map((item) => (
              <CheckRow key={item}>{item}</CheckRow>
            ))}
          </Box>
        </Panel>

        <Panel>
          <Box
            component="img"
            src={IMG.socializing}
            alt="Guests socializing at the Empire State Building show"
            sx={{
              width: "100%",
              height: { xs: 180, md: 220 },
              objectFit: "cover",
              borderRadius: "10px",
              mb: 2.25,
              display: "block",
            }}
          />
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: ACCENT,
              mb: 1,
            }}
          >
            Proposed · Empire State Residency × Airbnb · Four nights
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <CapitalOneLogo height={{ xs: 30, md: 36 }} />
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                lineHeight: 1.2,
              }}
            >
              Similar model, with a VIP layer
            </Typography>
          </Box>
          <Body sx={{ mb: 2.5 }}>
            Airbnb gets reserved Airbnb guest tickets across the residency, plus custom
            packages and an exclusive seating cutout. VIP guests can add green-room access and
            talent memorabilia moments.
          </Body>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: MUTED,
              mb: 1.25,
            }}
          >
            Reserved Airbnb guest access
          </Typography>
          <Box component="ul" sx={{ m: 0, mb: 2.5, pl: 0, listStyle: "none" }}>
            {UNITED_RESERVED.map((item) => (
              <CheckRow key={item}>{item}</CheckRow>
            ))}
          </Box>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: MUTED,
              mb: 1.25,
            }}
          >
            VIP layer
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
            {UNITED_VIP.map((item) => (
              <CheckRow key={item}>{item}</CheckRow>
            ))}
          </Box>
        </Panel>
      </Box>
    </Section>
  );
}


const AIRBNB_IDEAS = [
  {
    title: "Airbnb Experiences",
    body: "The residency is a night people plan a trip around. Featuring it on Airbnb Experiences puts it in front of guests who are already in New York, looking for something they cannot do at home.",
  },
  {
    title: "Dedicated tickets",
    body: "A protected block of tickets for Airbnb guests. They skip the public waitlist, the way a cardholder skips the line.",
  },
  {
    title: "Signage on site",
    body: "Airbnb on site for the night. At arrival, in the room, and on the way up to the observatory, guests see who got them in.",
  },
  {
    title: "Social with the comics",
    body: "The comedians are the faces of the night. A collaboration with them carries the show, and Airbnb, to their own audiences.",
  },
] as const;

function AirbnbExtraSection() {
  return (
    <Section id="other-ways">
      <Eyebrow accent>For Airbnb</Eyebrow>
      <Title>How Airbnb shows up.</Title>
      <Body sx={{ maxWidth: 760, mb: { xs: 3, md: 4 } }}>
        The walkthrough above is the night. This is the layer we would build for Airbnb guests.
      </Body>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, mb: { xs: 6, md: 8 } }}>
        {AIRBNB_IDEAS.map((idea) => (
          <Box key={idea.title} sx={{ bgcolor: CARD_BG, border: `1px solid ${LINE}`, borderRadius: RADIUS, p: { xs: 2.25, md: 3 } }}>
            <Typography sx={{ fontFamily: portalFont, fontWeight: 700, color: "#fff", fontSize: "1.15rem", mb: 1 }}>
              {idea.title}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, lineHeight: 1.55 }}>
              {idea.body}
            </Typography>
          </Box>
        ))}
      </Box>

      <Eyebrow accent>Other ways to work together</Eyebrow>
      <Title>Popup in Airbnb&apos;s coolest locations.</Title>
      <Body sx={{ maxWidth: 760, mb: { xs: 3, md: 4 } }}>
        We already do this with partners. We show up at their locations and popup for their users. They market it. We produce it. The venue gets fresh faces in the door. The guests get a night they would not have found on their own.
      </Body>
      <Panel sx={{ mb: { xs: 4, md: 5 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.3fr" },
            gap: { xs: 2.25, md: 3 },
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={IMG.stTheos}
            alt="A ComedyUO popup at St. Theo's for BILT members"
            sx={{
              width: "100%",
              height: { xs: 200, md: 260 },
              objectFit: "cover",
              borderRadius: "10px",
              display: "block",
            }}
          />
          <Box>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ACCENT,
                mb: 1,
              }}
            >
              Example · Neighborhood Comedy × BILT
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  lineHeight: 1.2,
                }}
              >
                Their location. Their users. Our show.
              </Typography>
              <Box
                component="img"
                src="/images/private-events-BILT_Rewards_Logo.png"
                alt="BILT"
                sx={{
                  height: { xs: 22, md: 26 },
                  width: "auto",
                  maxWidth: 120,
                  objectFit: "contain",
                  display: "block",
                  flexShrink: 0,
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Box>
            <Body sx={{ mb: 2 }}>
              We go to BILT&apos;s partner locations, like St. Theo&apos;s, and popup for their members. BILT markets it. We produce it.
            </Body>
            <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
              <CheckRow>Popup at a partner location, for their users</CheckRow>
              <CheckRow>They market it. We produce it.</CheckRow>
              <CheckRow>The venue wins: fresh faces in the door</CheckRow>
              <CheckRow>The guests win: a night built just for them</CheckRow>
            </Box>
          </Box>
        </Box>
      </Panel>
      <Body sx={{ maxWidth: 760, mb: 0 }}>
        We could run the same model in Airbnb&apos;s locations around the city as a tester. Airbnb markets it to guests. We produce the night. The property gets new people through the door. The guests get an experience only Airbnb could offer them.
      </Body>
    </Section>
  );
}

function Footer({ contactEmail }: { contactEmail: string }) {
  return (
    <Box component="footer" sx={{ borderTop: `1px solid ${LINE}`, py: { xs: 4, md: 5 } }}>
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <CapitalOneLogo height={{ xs: 24, md: 30 }} />
          <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.875rem" }}>
            ComedyUO × Airbnb · Partnership concept
          </Typography>
        </Box>
        <Link
          href={`mailto:${contactEmail}`}
          underline="hover"
          sx={{ fontFamily: portalFont, color: "#fff", fontSize: "0.875rem" }}
        >
          {contactEmail}
        </Link>
      </Box>
    </Box>
  );
}

function MarchLineupGrid() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr 1fr",
          md: "repeat(3, 1fr)",
        },
        gap: { xs: 1.5, md: 2 },
        alignItems: "stretch",
      }}
    >
      {MARCH_LINEUP.map((item) => (
        <Link
          key={item.name}
          href={item.instagram}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          sx={{
            display: "flex",
            flexDirection: "column",
            color: "inherit",
            borderRadius: RADIUS,
            border: `1px solid ${LINE}`,
            bgcolor: CARD_BG,
            overflow: "hidden",
            height: "100%",
            transition: "border-color 0.15s ease",
            "&:hover": { borderColor: "rgba(255,255,255,0.35)" },
          }}
        >
          <Box
            sx={{
              aspectRatio: "3 / 4",
              bgcolor: "#121212",
              borderBottom: `1px solid ${LINE}`,
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={item.src}
              alt={item.name}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: item.objectPosition,
                display: "block",
              }}
            />
          </Box>
          <Box
            sx={{
              p: { xs: 1.5, md: 1.75 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              minHeight: { xs: 84, md: 92 },
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "0.95rem", md: "1.0625rem" },
                mb: 0.4,
                lineHeight: 1.25,
              }}
            >
              {item.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.75rem",
                color: MUTED,
                mb: 0.75,
              }}
            >
              {item.note}
            </Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: ACCENT,
              }}
            >
              Instagram
            </Typography>
          </Box>
        </Link>
      ))}
    </Box>
  );
}

function Section({
  id,
  children,
  sx = {},
}: {
  id: string;
  children: React.ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Box
      id={id}
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2.5, md: 4 },
        py: { xs: 8, md: 12 },
        scrollMarginTop: 88,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function Panel({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: RADIUS,
        border: `1px solid ${LINE}`,
        bgcolor: CARD_BG,
        height: "100%",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function CheckRow({ children }: { children: React.ReactNode }) {
  return (
    <Box component="li" sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1.1 }}>
      <CheckIcon sx={{ fontSize: 18, color: "#fff", mt: "2px", flexShrink: 0 }} />
      <Typography sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.5, fontSize: "0.95rem" }}>
        {children}
      </Typography>
    </Box>
  );
}

function MiniPhoto({ src, alt }: { src: string; alt: string }) {
  return (
    <Box
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        aspectRatio: "1 / 1",
        border: `1px solid ${LINE}`,
        bgcolor: "#121212",
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </Box>
  );
}

function CaseFigureCard({
  card,
}: {
  card: (typeof CASE_CHAPTERS)[number];
}) {
  const [open, setOpen] = useState(false);
  const longFigure = card.figure.length > 4;

  return (
    <Box
      sx={{
        borderRadius: RADIUS,
        border: `1px solid ${LINE}`,
        bgcolor: CARD_BG,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        component="img"
        src={card.imageSrc}
        alt={card.imageAlt}
        sx={{
          width: "100%",
          height: { xs: 160, md: 180 },
          objectFit: "cover",
          display: "block",
          flexShrink: 0,
        }}
      />
      <Box sx={{ p: { xs: 2, md: 2.5 }, display: "flex", flexDirection: "column", flex: 1 }}>
        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: MUTED,
            mb: 1,
          }}
        >
          {card.title}
        </Typography>
        <Typography
          component="p"
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: longFigure
              ? { xs: "1.75rem", md: "2.15rem" }
              : { xs: "3.25rem", md: "4rem" },
            letterSpacing: "-0.04em",
            lineHeight: 1,
            mb: 0.75,
          }}
        >
          {card.figure}
        </Typography>
        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.875rem",
            color: ACCENT,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            fontWeight: 600,
            mb: 2,
          }}
        >
          {card.figureLabel}
        </Typography>

        <Box sx={{ mt: "auto" }}>
          <Button
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  transform: open ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
            }
            sx={{
              fontFamily: portalFont,
              fontWeight: 600,
              textTransform: "none",
              color: "#fff",
              px: 0,
              minWidth: 0,
              justifyContent: "flex-start",
              "&:hover": { bgcolor: "transparent", color: ACCENT },
            }}
          >
            {open ? "Hide details" : "Read more"}
          </Button>
          <Collapse in={open}>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: "0.9375rem",
                lineHeight: 1.65,
                color: MUTED,
                pt: 1.25,
                pb: 0.5,
              }}
            >
              {card.copy}
            </Typography>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}

function CapitalOneLogo({
  height,
}: {
  height: { xs: number; md: number } | number;
}) {
  const h = typeof height === "number" ? { xs: height, md: height } : height;
  return (
    <Box
      component="img"
      src={IMG.capitalOneLogo}
      alt="Airbnb"
      sx={{
        height: h,
        width: "auto",
        display: "block",
        flexShrink: 0,
        objectFit: "contain",
      }}
    />
  );
}

function Eyebrow({
  children,
  accent = false,
  sx = {},
}: {
  children: React.ReactNode;
  accent?: boolean;
  sx?: Record<string, unknown>;
}) {
  return (
    <Typography
      sx={{
        fontFamily: portalFont,
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: accent ? ACCENT : MUTED,
        mb: 1.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function Title({
  children,
  sx = {},
}: {
  children: React.ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: portalFont,
        fontWeight: 700,
        color: "#fff",
        fontSize: "clamp(1.85rem, 2.8vw + 1rem, 3rem)",
        letterSpacing: "-0.03em",
        lineHeight: 1.1,
        mb: { xs: 2, md: 2.5 },
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function Body({
  children,
  sx = {},
}: {
  children: React.ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Typography
      sx={{
        fontFamily: portalFont,
        color: MUTED,
        fontSize: { xs: "0.975rem", md: "1.0625rem" },
        lineHeight: 1.65,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

function PillButton({
  href,
  children,
  solid = false,
}: {
  href: string;
  children: React.ReactNode;
  solid?: boolean;
}) {
  return (
    <Button
      href={href}
      endIcon={solid ? <SouthIcon sx={{ fontSize: 15 }} /> : undefined}
      sx={{
        fontFamily: portalFont,
        fontWeight: 700,
        textTransform: "none",
        borderRadius: "999px",
        px: 2.75,
        py: 1.2,
        fontSize: "0.9375rem",
        ...(solid
          ? {
              bgcolor: "#fff",
              color: "#0a0a0a",
              "&:hover": { bgcolor: "#fff", filter: "brightness(0.94)" },
            }
          : {
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.45)",
              "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
            }),
      }}
    >
      {children}
    </Button>
  );
}